/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'dashboard-analytics/visualizations/vipr/VIPRUtils',
	'../../../../widgets/livewidget/nls/StringResources',
	'dashboard-analytics/util/FontFamilyOptions',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../content/slotActions/api/SlotActionsProviderAPI',
	'../../../../util/ContentUtil'
], (_, VIPRUtils, stringResources, FontFamilyOptions, APIFactory, SlotActionsProviderAPI, ContentUtil) => {

	class TextAction {

		initialState = {
			fontSize: null,
			font: null,
			bold: false,
			italic: false,
			underline: false,
			justifyLeft: false,
			justifyCenter: false,
			justifyRight: false,
			color: null,
			textAlign: '',
			textWrap: false
		};

		localVisSuffix = {
			color: 'Color',
			fontSize: 'FontSize',
			font: 'FontFace',
			bold: 'FontBold',
			italic: 'FontItalic',
			underline: 'FontUnderline',
			textAlign: 'FontAlign',
			textWrap: 'TextWrap'
		};

		//vida based visulization does not support text decoration and text alignment yet, widget.legend has the same naming pattern
		vidaVisSuffix = {
			color: '.color',
			fontSize: 'size',
			font: 'family',
			bold: 'weight',
			italic: 'style'
		};

		booleanProperty = ['weight', 'style'];

		selectedAreaPrefix = 'selectedArea';

		constructor(options) {
			this.dashboard = options.dashboardAPI;
			this.content = options.content;
			this.features = options.features;
			this.colorsService = this.dashboard.getFeature('Colors');

			this.content.getFeature('DataPointActions').registerProvider('TextAction', this);
			this.content.getFeature('SlotActions').registerProvider('TextAction', this.getAPI());
		}

		getDataPointActionList(selections, options) {
			if(this._supportDataPointAction()) {
				if (options.isAreaSelected){
					this.selectedArea = ContentUtil.getContextFromSelection(this.content, this.dashboard);
					// if the selection is not valid, disable the textAction
					if(!this.selectedArea.length){
						return [];
					}
					this.isAreaSelected = true;
				} else {
					this.isAreaSelected = false;
					this.selectedArea = null;
				}
				return this.getContribution(null, selections.area);
			}

			return [];
		}

		_supportDataPointAction() {
			if(this.dashboard.getMode() !== this.dashboard.MODES.EDIT) {
				return false;
			}

			return true;
		}

		getSlotActionList(slotId, index, options) {
			if (this._supportSlotAction(index)) {
				if (options && options.isAreaSelected) {
					// textAction is not actually a slot action, should only have measure title selection here
					this.selectedArea = this._getContextFromSlot(slotId, index);
					// if the selection is not valid, disable the textAction
					if(!this.selectedArea.length){
						return [];
					}
					this.isAreaSelected = true;
				} else {
					this.isAreaSelected = false;
					this.selectedArea = null;
				}
				return this.getContribution(slotId, options && options.area);
			}

			return [];
		}

		_getContextFromSlot(slotId, index){
			const slot = this.content.getFeature('Visualization').getSlots().getSlot(slotId);
			const dataItemList = slot && slot.getDataItemList();
			const dataItemLabel = dataItemList && dataItemList[index] && dataItemList[index].getLabel();
			if (dataItemLabel){
				return [dataItemLabel];
			}
		}

		_getVisDefinition() {
			return this.content.getFeature('Visualization').getDefinition();
		}

		_supportSlotAction(index) {

			if (this._getVisDefinition().getId() === 'summary') {
				return false;
			}

			if(this.dashboard.getMode() !== this.dashboard.MODES.EDIT) {
				return false;
			}

			if(Array.isArray(index) && index.length == 1) {
				return true;
			}

			if(index !== undefined) {
				return false;
			}

			return true;
		}

		_supportsTextWrap(slotId, area) {
			const supportsTextWrapAreaList = this._getVisDefinition().getProperty('supportsTextWrap');
			return supportsTextWrapAreaList ? supportsTextWrapAreaList[area] || supportsTextWrapAreaList[slotId] : false;
		}

		getContribution(slotId, area) {
			this.propertyPrefix = this.isAreaSelected ? this.selectedAreaPrefix : this.constructPropertyPrefix(slotId, area);
			const visualization = this.content.getFeature('Visualization');
			this.groupedProperty = visualization.getDefinition().getProperty('groupedProperty');
			if (this.propertyPrefix) {
				this.currentProps = this.content.getPropertyNameList().map(prop => { return { id: prop, value: this.content.getPropertyValue(prop) }; });
				this.isLocalVis = this._isLocalVis(visualization.getDefinition().getId());
				this.propertySuffix = this.isLocalVis ? this.localVisSuffix : this.vidaVisSuffix;
				const currentState = this._buildStateFromProps(visualization, this.currentProps);
				const defaultState = this._getDefaultState(visualization);

				const elementListType = this._getVisDefinition().getProperty('supportsAdvancedProperties') ? 'default' : 'simpleTextEdit';
				const colorOptions = this._getTextColorOptions();
				const fontSizeOptions = this._getAppropriateFontSizes(area);
				const supportsTextWrap = this._supportsTextWrap(slotId, area);
				const viewOptions = {
					height: 140,
					width: 160,
					currentState: currentState,
					properties: {
						colors: colorOptions,
						fontSizes: fontSizeOptions,
						fonts: FontFamilyOptions,
						defaultState
					},
					elementListType: elementListType,
					supportsTextWrap: supportsTextWrap,
					flyoutActions: {
						close: true
					}
				};
				const commonTextEdit = this.features['Dashboard.Icons'].getIcon('common-text-edit');
				return [{
					name: 'text',
					text: stringResources.get('toolbarTextActionText'),
					label: stringResources.get('toolbarTextActionText'),
					targetCaption: this._getTargetCaptionForPrefix(),
					icon: commonTextEdit.id,
					type: 'NextView',
					view: {
						module: 'dashboard-analytics/lib/@waca/dashboard-common/dist/ui/dialogs/TextFormatDialog',
						state: viewOptions
					},
					actions: {
						apply: this.onStateChange.bind(this)
					},
					showCaption:false
				}];

			}
		}

		//Translated strings exist for most propertyPrefixes like widget.legend or labels
		//If they exist, return them.  Otherwise, return null.
		_getTargetCaptionForPrefix() {
			const translatedStyleText =  stringResources.get('styletext-' + this.propertyPrefix);
			if (translatedStyleText.indexOf('__NOT_TRANSLATED__') === -1) {
				return translatedStyleText;
			}
			//Can't find a translation for this prefix.
			//Return null which will show the Style Text heading with no area.
			return null;
		}

		/**
		 * This method returns the appropriate font sizes that need to be shown. For the area type legend we are going to show 3 options and for the rest, 4 options.
		 * @param {string} area
		 * returns the Font Size object
		 */
		_getAppropriateFontSizes(area) {
			const FONT_SIZE_OPTIONS = [
				{ value: '12px', label: '12px' },
				{ value: '14px', label: '14px' },
				{ value: '16px', label: '16px' },
				{ value: '24px', label: '24px' }
			];

			const LEGEND_FONT_SIZE_OPTIONS = [
				{ value: '11px', label: '11px' },
				{ value: '12px', label: '12px' },
				{ value: '14px', label: '14px' },
				{ value: '16px', label: '16px' }
			];

			return area === 'legend' ? LEGEND_FONT_SIZE_OPTIONS : FONT_SIZE_OPTIONS;
		}

		_buildStateFromProps(vis, props) {
			const visId = vis.getDefinition().getId();
			const isLocalVis = this._isLocalVis(visId);
			const propertySuffix = isLocalVis ? this.localVisSuffix : this.vidaVisSuffix;
			const state = {};
			_.each(propertySuffix, (value, prop) => {
				state[prop] = this.findCurrentProperties(props, value);
			});
			return state;
		}

		_getDefaultState(vis) {
			const visId = vis.getDefinition().getId();
			if (this._isLocalVis(visId)) {
				return this._getDefaultLocalVisState();
			} else {
				return this._getDefaultVIPRState(vis);
			}
		}

		_getDefaultLocalVisState() {
			const state = {
				font: 'IBM Plex Sans,Helvetica Neue,Arial,sans-serif',
				fontSize: '12px',
				color: '#000000',
				bold: false,
				textAlign: ''
			};

			// Original table for all visualizations can be found (if needed) here: https://github.ibm.com/BusinessAnalytics/dashboard-livewidget/pull/7081/files#diff-ddaf13ed708ed9f015e9a988a4cc1b9bR244
			switch (this.propertyPrefix) {
				case 'summary':
					state.bold = true;
					break;
				case 'listValue':
					state.fontSize = '16px'; // Carbonize value is 12pt which is 16px
					break;
			}

			return state;
		}

		_getDefaultVIPRState(vis) {
			const propertySupport = this.content.getFeature('Visualization.legacy').getLegacyManagers().visPropertySupport;

			const defaultProps = this.content.getPropertyNameList()
				.map(prop => ({
					id: prop,
					value: propertySupport.getPropertyDefaultValue(prop)
				}));

			return this._buildStateFromProps(vis, defaultProps);
		}

		constructPropertyPrefix(currentSlotId, area) {
			let propertyPrefixesForWidget = this._getVisDefinition().getProperty('propertyPrefixes');
			let result;

			if(propertyPrefixesForWidget){
				result = propertyPrefixesForWidget[area];
				if(result) {
					return result;
				} else {
					if(!currentSlotId && area === 'visualization') {
						return propertyPrefixesForWidget['dataPoint'];
					} else if (currentSlotId) {
						return propertyPrefixesForWidget[currentSlotId];
					}
				}
			}
		}

		_isLocalVis(visId){
			const localVis=['summary','list','crosstab','JQGrid'];
			return _.contains(localVis,visId);
		}

		findCurrentProperties(currentProps, propertyToFind) {
			const isFontProp = this.isLocalVis === false && propertyToFind !== '.color';
			const isColorProp = propertyToFind === '.color' || propertyToFind === 'Color';

			if (propertyToFind)	{
				var findValue = this.propertyPrefix + (isFontProp ? '.font' : propertyToFind);
			} else {
				return undefined;
			}

			var propertyFound = this.findPropertyById(currentProps, findValue);
			if (currentProps === undefined || propertyFound === undefined) {
				var prefix = _.invert(this.propertySuffix)[propertyToFind];
				return this.initialState[prefix];
			} else if ( propertyFound ) {
				if (!isFontProp) {
					if(this.isAreaSelected){
						// if multiple selections are made, show the first selection's value
						propertyFound.value = propertyFound.value && propertyFound.value[this.selectedArea[0]] && propertyFound.value[this.selectedArea[0]].value;
					}
					// if the property is a color property, we need to transform the color class value into hex value
					if (isColorProp && propertyFound.value !== null && typeof propertyFound.value === 'string' && !this._isHexColor(propertyFound.value)) {
						return this.colorsService.getHexColorFromDashboardColorSet(propertyFound.value);
					}
					return propertyFound.value;
				} else {
					return VIPRUtils.getVidaFontPropertiesPart(propertyToFind, propertyFound.value);
				}
			}
		}

		_isHexColor(val) {
			return val.indexOf('#') === 0 && val.length === 7;
		}

		findPropertyById(properties, id) {
			var foundProperty = _.find(properties, function(property) {
				return property.id === id;
			});
			return foundProperty;
		}

		_getTextColorOptions(){
			const colorPalette = this.colorsService.getDashboardColorSet().filter(item => item.id !== 'transparent');
			var colorOptions = [];
			_.each(colorPalette,(color)=> {
				colorOptions.push(color.hexValue);
			});
			return colorOptions;
		}

		onStateChange(stateChanges) {
			let propertyList = [];
			if(this.propertyPrefix) {
				if (this.isLocalVis) {
					propertyList = this._buildLocalVisPropertyList(stateChanges, propertyList);
				} else {
					propertyList = this._buildVidaVisPropertyList(stateChanges, propertyList);
				}
			}
			this.editProperties(propertyList);
		}

		_buildLocalVisPropertyList(stateChanges, propertyList){
			const changedStates = Object.keys(stateChanges);
			_.each(changedStates,changedState => {
				if(this.propertySuffix[changedState]){
					const propertyName = this.propertyPrefix + this.propertySuffix[changedState];
					const isColorProp = changedState === 'color';

					// the text toolbar will set the hex color value, we need to transform the value into color class value before saved in the model
					let newPropertyVal = isColorProp && _.isString(stateChanges[changedState]) ? this._findColorIdFromHex(stateChanges[changedState]) : stateChanges[changedState];

					if(this.isAreaSelected){
						const currentPropertyValue = JSON.parse(JSON.stringify(this.content.getPropertyValue(propertyName) || {}));
						_.each(this.selectedArea, selectedArea => {
							// the text formatting for selection need to be applied in order, whichever comes last should win, use timestamp as the weight to sort the order here
							currentPropertyValue[selectedArea] = {
								value: newPropertyVal,
								weight: Date.now()
							};
						});
						newPropertyVal = currentPropertyValue;
					}
					propertyList.push({ 'id': propertyName,'value':newPropertyVal });
					if(this.groupedProperty && _.has(this.groupedProperty, propertyName)) {
						_.each(this.groupedProperty[propertyName], (prop) => {
							propertyList.push({ 'id': prop,'value':newPropertyVal });
						});
					}
				}
			});
			return propertyList;
		}

		_buildVidaVisPropertyList(stateChanges, propertyList){

			const vidaPropertyList = [];
			const fontParts = _.pick(stateChanges, 'font', 'fontSize');
			const colorPart = _.pick(stateChanges, 'color');

			if(!_.isEmpty(fontParts)){
				const fontPropName = this.propertyPrefix + '.font';
				const currentPropertyValue = this.content.getPropertyValue(fontPropName);
				// VIDA does not set the property correctly if we pass null to it.
				// VIPRUtils.buildVidaFontPropertiesFromParts does not handle undefined properly for font, it simply returns the current font.
				// We need this if statement to make sure that we only call buildVidaFontPropertiesFromParts when necessary.
				// If it is not necessary, we can just set the property to undefined and it will set it to the default values.
				let fontProperty = undefined;
				if (stateChanges.font || stateChanges.fontSize) {
					fontProperty = VIPRUtils.buildVidaFontPropertiesFromParts(fontParts, currentPropertyValue);
				}
				vidaPropertyList.push({ id: fontPropName, value: fontProperty });
			}

			if(!_.isEmpty(colorPart)){
				//build color property
				const colorPropName = this.propertyPrefix + '.color';
				// the text toolbar will set the hex color value, we need to transform the value into color class value before saved in the model
				const colorPropValue = _.isString(colorPart['color']) ? this._findColorIdFromHex(colorPart['color']) : colorPart['color'];
				vidaPropertyList.push({ id: colorPropName, value: colorPropValue });
			}

			vidaPropertyList.forEach(vidaProperty => {
				if(this.groupedProperty && _.has(this.groupedProperty, vidaProperty.id)) {
					_.each(this.groupedProperty[vidaProperty.id], id => vidaPropertyList.push({ 'id': id,'value': vidaProperty.value }));
				}
			});

			return propertyList.concat(vidaPropertyList);
		}

		_findColorIdFromHex(colorValue) {
			const colorPalette = this.colorsService.getDashboardColorSet().filter(item => item.id !== 'transparent');
			const found = _.find(colorPalette, color => {
				return color.hexValue.toUpperCase() === colorValue.toUpperCase();
			});

			if (!found) {
				return this.colorsService.getColorClassName(colorValue);
			} else {
				return found.id;
			}
		}

		/**
		 * This method sets the vis properties to the value given
		 * @param propertyList - the propertyList that trigerred the state change
		 */
		editProperties(propertyList) {
			const transactionToken = this.dashboard.getFeature('Transaction').startTransaction();
			propertyList.forEach(prop => {
				this.content.setPropertyValue(prop.id, prop.value, transactionToken);
			});
			this.dashboard.getFeature('Transaction').endTransaction(transactionToken);
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [SlotActionsProviderAPI]);
			}
			return this._api;
		}

	}

	return TextAction;
});
